import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Chip,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Tooltip,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import api from '../api';
import InterviewStoryChatPanel from './InterviewStoryChatPanel';

const THEMES = [
  'leadership', 'ownership', 'collaboration', 'communication',
  'problem-solving', 'conflict-resolution', 'data-driven',
  'adaptability', 'customer-focus', 'innovation',
];

const THEME_COLORS = {
  leadership: '#6366f1', ownership: '#f97316', collaboration: '#10b981',
  communication: '#06b6d4', 'problem-solving': '#8b5cf6', 'conflict-resolution': '#ef4444',
  'data-driven': '#3b82f6', adaptability: '#f59e0b', 'customer-focus': '#ec4899', innovation: '#14b8a6',
};

function StoryDraftCard({ index, story, selected, onToggle, onChange, onOpenChat }) {
  const [expanded, setExpanded] = useState(true);

  function update(field, value) {
    onChange(index, { ...story, [field]: value });
  }

  function toggleTheme(t) {
    const themes = story.themes || [];
    update('themes', themes.includes(t) ? themes.filter(x => x !== t) : [...themes, t]);
  }

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${selected ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.06)'}`,
        bgcolor: selected ? '#131929' : 'rgba(255,255,255,0.02)',
        opacity: selected ? 1 : 0.5,
        transition: 'all 0.2s',
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, pt: 2, pb: 1, gap: 1 }}>
          <Checkbox
            checked={selected}
            onChange={onToggle}
            size="small"
            sx={{
              color: 'var(--tx-ft)',
              '&.Mui-checked': { color: '#6366f1' },
              p: 0.5,
            }}
          />
          <Typography variant="subtitle2" sx={{ color: 'var(--tx-lo)', fontWeight: 600, mr: 1 }}>
            Story {index + 1}
          </Typography>
          {selected && (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: '12px !important' }} />}
              label="Will be saved"
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                bgcolor: 'rgba(99,102,241,0.15)',
                color: '#818cf8',
                border: '1px solid rgba(99,102,241,0.3)',
              }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          <Tooltip title="AI Edit — improve with AI">
            <IconButton
              size="small"
              onClick={onOpenChat}
              sx={{
                color: '#6366f1',
                bgcolor: 'rgba(99,102,241,0.1)',
                border: '1px solid rgba(99,102,241,0.25)',
                borderRadius: 1.5,
                width: 30,
                height: 30,
                mr: 0.5,
                '&:hover': { bgcolor: 'rgba(99,102,241,0.2)', borderColor: 'rgba(99,102,241,0.5)' },
              }}
            >
              <AutoFixHighIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            onClick={() => setExpanded(e => !e)}
            endIcon={<ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
            sx={{ color: 'var(--tx-ft)', textTransform: 'none', fontSize: '0.75rem' }}
          >
            {expanded ? 'Collapse' : 'Expand'}
          </Button>
        </Box>

        {/* Editable fields */}
        {expanded && (
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <Stack spacing={2}>
              {/* Title */}
              <TextField
                label="Title"
                value={story.title || ''}
                onChange={e => update('title', e.target.value)}
                fullWidth
                size="small"
                required
              />

              {/* Alternative titles — dynamic list */}
              <Box>
                <Typography variant="caption" sx={{ color: 'var(--tx-dim)', display: 'block', mb: 0.75 }}>
                  Alternative Titles
                </Typography>
                <Stack spacing={0.75}>
                  {(story.alternative_titles || []).map((alt, ai) => (
                    <Stack key={ai} direction="row" alignItems="center" gap={0.75}>
                      <TextField
                        value={alt}
                        onChange={e => {
                          const next = [...(story.alternative_titles || [])];
                          next[ai] = e.target.value;
                          update('alternative_titles', next);
                        }}
                        fullWidth
                        size="small"
                        placeholder="Alternative title…"
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          const next = (story.alternative_titles || []).filter((_, i) => i !== ai);
                          update('alternative_titles', next);
                        }}
                        sx={{ color: 'var(--tx-ft)', '&:hover': { color: '#ef4444' }, flexShrink: 0 }}
                      >
                        <CloseIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
                <Button
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  onClick={() => update('alternative_titles', [...(story.alternative_titles || []), ''])}
                  sx={{ mt: 0.75, color: '#818cf8', textTransform: 'none', fontSize: '0.75rem', px: 0.5, '&:hover': { bgcolor: 'rgba(99,102,241,0.08)' } }}
                >
                  Add alternative title
                </Button>
                {/* LLM-suggested chips */}
                {story._suggested_alts && story._suggested_alts.length > 0 && (
                  <Box sx={{ mt: 0.75 }}>
                    <Typography variant="caption" sx={{ color: 'var(--tx-ft)', fontSize: '0.65rem' }}>
                      Suggestions — click to add:
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {story._suggested_alts.map((alt, ai) => (
                        <Chip
                          key={ai}
                          label={alt}
                          size="small"
                          onClick={() => {
                            const existing = story.alternative_titles || [];
                            if (!existing.includes(alt)) {
                              update('alternative_titles', [...existing, alt]);
                            }
                          }}
                          sx={{
                            fontSize: '0.65rem',
                            bgcolor: 'rgba(99,102,241,0.07)',
                            color: '#818cf8',
                            border: '1px solid rgba(99,102,241,0.2)',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'rgba(99,102,241,0.14)', borderColor: 'rgba(99,102,241,0.4)' },
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>

              <TextField
                label="Tags (comma-separated)"
                value={Array.isArray(story.tags) ? story.tags.join(', ') : story.tags || ''}
                onChange={e => update('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                fullWidth
                size="small"
                placeholder="leadership, fintech, product"
              />

              <Box>
                <Typography variant="caption" sx={{ color: 'var(--tx-dim)', display: 'block', mb: 0.75 }}>
                  Themes
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {THEMES.map(t => (
                    <Chip
                      key={t}
                      label={t}
                      size="small"
                      onClick={() => toggleTheme(t)}
                      sx={{
                        cursor: 'pointer',
                        fontSize: '0.65rem',
                        bgcolor: (story.themes || []).includes(t) ? `${THEME_COLORS[t]}22` : 'rgba(255,255,255,0.04)',
                        color: (story.themes || []).includes(t) ? THEME_COLORS[t] : 'var(--tx-dim)',
                        border: '1px solid',
                        borderColor: (story.themes || []).includes(t) ? `${THEME_COLORS[t]}44` : 'transparent',
                      }}
                    />
                  ))}
                </Stack>
              </Box>

              <TextField
                label="Full Story"
                value={story.full_story || ''}
                onChange={e => update('full_story', e.target.value)}
                fullWidth
                multiline
                minRows={4}
              />

              <TextField
                label="Interview Form"
                value={story.interview_form || ''}
                onChange={e => update('interview_form', e.target.value)}
                fullWidth
                multiline
                minRows={3}
                helperText="Bullet points for verbal delivery"
              />

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

              <Grid container spacing={1.5}>
                {['situation', 'task', 'action', 'result'].map(field => (
                  <Grid key={field} size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label={field.charAt(0).toUpperCase() + field.slice(1)}
                      value={story[field] || ''}
                      onChange={e => update(field, e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                    />
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default function InterviewReviewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { stories: initialStories = [], sourceFilename, chunksProcessed } = location.state || {};

  // Move LLM-suggested alternative_titles into _suggested_alts so they show as
  // clickable chips; the editable field starts empty until the user fills it in.
  const [drafts, setDrafts] = useState(() =>
    initialStories.map(s => ({
      ...s,
      _suggested_alts: s.alternative_titles || [],
      alternative_titles: [],
    }))
  );
  const [selected, setSelected] = useState(() => new Set(initialStories.map((_, i) => i)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [chatStoryIndex, setChatStoryIndex] = useState(null);

  function openChat(index) { setChatStoryIndex(index); }
  function closeChat() { setChatStoryIndex(null); }

  function applyStoryUpdate(update) {
    if (chatStoryIndex === null) return;
    setDrafts(prev => prev.map((d, i) =>
      i === chatStoryIndex ? { ...d, ...update } : d
    ));
  }

  if (!location.state || initialStories.length === 0) {
    navigate('/interview/upload', { replace: true });
    return null;
  }

  function toggleSelect(index) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === drafts.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(drafts.map((_, i) => i)));
    }
  }

  function updateDraft(index, updated) {
    setDrafts(prev => prev.map((d, i) => i === index ? updated : d));
  }

  async function handleSave() {
    // Strip UI-only fields before persisting
    const toSave = drafts
      .filter((_, i) => selected.has(i))
      .map(({ _suggested_alts, ...rest }) => rest);
    if (toSave.length === 0) return;

    setSaving(true);
    setError('');
    try {
      await api.post('/api/interview/stories/batch', {
        stories: toSave,
        source_filename: sourceFilename || null,
      });
      navigate('/interview');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save stories');
    } finally {
      setSaving(false);
    }
  }

  const selectedCount = selected.size;

  return (
    <Box sx={{ bgcolor: '#0d1117', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--tx-hi)', mb: 0.5 }}>
            Review Extracted Stories
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--tx-dim)' }}>
            {drafts.length} {drafts.length === 1 ? 'story' : 'stories'} detected
            {sourceFilename ? ` from ${sourceFilename}` : ''}
            {chunksProcessed > 1 ? ` · Processed in ${chunksProcessed} batches` : ''}
          </Typography>
        </Box>

        {/* Select all + action bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 3,
            px: 2,
            py: 1.5,
            bgcolor: '#131929',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 2,
            gap: 2,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedCount === drafts.length}
                indeterminate={selectedCount > 0 && selectedCount < drafts.length}
                onChange={toggleAll}
                size="small"
                sx={{ color: 'var(--tx-ft)', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#6366f1' } }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: 'var(--tx-lo)' }}>
                {selectedCount === drafts.length ? 'Deselect all' : 'Select all'}
              </Typography>
            }
          />
          <Typography variant="body2" sx={{ color: 'var(--tx-dim)', flex: 1 }}>
            {selectedCount} of {drafts.length} selected
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/interview/upload')}
            sx={{ color: 'var(--tx-dim)', borderColor: 'rgba(255,255,255,0.1)' }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
            onClick={handleSave}
            disabled={saving || selectedCount === 0}
            sx={{
              bgcolor: '#6366f1',
              '&:hover': { bgcolor: '#4f46e5' },
              '&.Mui-disabled': { bgcolor: 'rgba(99,102,241,0.3)', color: 'rgba(255,255,255,0.4)' },
            }}
          >
            {saving ? 'Saving…' : `Save ${selectedCount} ${selectedCount === 1 ? 'Story' : 'Stories'}`}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Draft cards */}
        <Stack spacing={2}>
          {drafts.map((story, i) => (
            <StoryDraftCard
              key={i}
              index={i}
              story={story}
              selected={selected.has(i)}
              onToggle={() => toggleSelect(i)}
              onChange={updateDraft}
              onOpenChat={() => openChat(i)}
            />
          ))}
        </Stack>

        {/* Bottom save bar */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 3, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
            onClick={handleSave}
            disabled={saving || selectedCount === 0}
            sx={{
              bgcolor: '#6366f1',
              '&:hover': { bgcolor: '#4f46e5' },
              '&.Mui-disabled': { bgcolor: 'rgba(99,102,241,0.3)', color: 'rgba(255,255,255,0.4)' },
              px: 4,
            }}
          >
            {saving ? 'Saving…' : `Confirm & Save ${selectedCount} ${selectedCount === 1 ? 'Story' : 'Stories'}`}
          </Button>
        </Box>
      </Container>

      <InterviewStoryChatPanel
        open={chatStoryIndex !== null}
        onClose={closeChat}
        story={chatStoryIndex !== null ? drafts[chatStoryIndex] : null}
        onApplyUpdate={applyStoryUpdate}
      />
    </Box>
  );
}
